import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Bar,
    Line,
    Scatter,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
import { formatNumber } from '../produksi';
import { RawDatum } from './CangkangTrend';
import { CustomTooltipDaily } from '../CustomTooltip';

export interface CangkangHarianDatum extends RawDatum {
    date: string;
}

interface Props {
    data: CangkangHarianDatum[];
}

const COLORS = {
    Produksi: '#82CA9D',
    Pakai: '#8884D8',
    Stok: '#CA6060',
    Black: '#000000',
    Kirim: '#FFC658',
    jamOlah: '#0088FE',
};

const CangkangProduksiHarian: React.FC<Props> = ({ data }) => {
    const [hoveringDataKey, setHoveringDataKey] = React.useState<DataKey<any> | undefined>(undefined);

    let produksiOpacity = 1;
    let paiOpacity = 1;
    let kirimOpacity = 1;
    let jamOpacity = 1;

    if (hoveringDataKey === 'Produksi') {
        paiOpacity = 0.5;
        kirimOpacity = 0.5;
        jamOpacity = 0.5;
    } else if (hoveringDataKey === 'Pakai') {
        produksiOpacity = 0.5;
        kirimOpacity = 0.5;
        jamOpacity = 0.5;
    } else if (hoveringDataKey === 'Kirim') {
        produksiOpacity = 0.5;
        paiOpacity = 0.5;
        jamOpacity = 0.5;
    } else if (hoveringDataKey === 'Jam Olah') {
        produksiOpacity = 0.5;
        paiOpacity = 0.5;
        kirimOpacity = 0.5;
    }

    const handleMouseEnter = (payload: any) => {
        setHoveringDataKey(payload.dataKey);
    };

    const handleMouseLeave = () => {
        setHoveringDataKey(undefined);
    };
    const formattedData = useMemo(() => {
        // pivot raw API → wide format per date
        const tmp: Record<string, any> = {};
        data.forEach(({ date, descr, qty }) => {
            if (!tmp[date]) {
                tmp[date] = { date, Stok: 0, Produksi: 0, Kirim: 0, Pakai: 0, jamOlah: 0 };
            }
            switch (descr) {
                case 'Stok':
                    tmp[date].Stok = qty/1000;
                    break;
                case 'Produksi':
                    tmp[date].Produksi = qty/1000;
                    break;
                case 'Kirim':
                    tmp[date].Kirim = qty/1000;
                    break;
                case 'Pakai':
                    tmp[date].Pakai = qty/1000;
                    break;
                case 'Jam Olah':
                    tmp[date].jamOlah = qty;
                    break;
            }
        });
        return Object.values(tmp).map(item => ({
            ...item,
            Pakai: item.Pakai,
            Kirim: item.Kirim
        })).sort(
            (a, b) => parseInt(a.date, 10) - parseInt(b.date, 10)
        );
    }, [data]);

    // fallback ke bulan/tahun sekarang jika data kosong
    const now = new Date();
    const defaultYear = now.getFullYear().toString();
    const defaultMonthNum = String(now.getMonth() + 1).padStart(2, '0');

    const rawPeriod = data[0]?.periode;
    const year = rawPeriod ? rawPeriod.slice(0, 4) : defaultYear;
    const monthNum = rawPeriod ? rawPeriod.slice(4) : defaultMonthNum;

    const monthNames = [
        'Januari','Februari','Maret','April','Mei','Juni',
        'Juli','Agustus','September','Oktober','November','Desember'
    ];
    const monthName = monthNames[parseInt(monthNum, 10) - 1];

    return (
        <Box p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" overflowX="auto" overflowY="hidden">
            <Text fontSize="xl" fontWeight="semibold" mb={4} color="gray.700">
                Produksi Cangkang Harian ({monthName} {year})
            </Text>
            <Box
                // flex={1} 
                minH="130px" 
                height="350px"
                minWidth="700px"
                overflowX="auto" 
                overflowY="hidden"
                w="100%"
            >
                <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={formattedData} barGap={0}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis
                            dataKey="date"
                            interval={0}
                            height={80}
                            tick={{ fontSize: 12 }}
                        />

                        {/* YAxis kiri untuk ton */}
                        <YAxis
                            yAxisId="left"
                            tickFormatter={v => formatNumber(Number(v), 'number')}
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Ton', angle: -90, position: 'insideLeft', offset: 5 }}
                            width={60}
                        />

                        {/* YAxis kanan untuk jam & persen */}
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12 }}
                            tickFormatter={v =>
                                typeof v === 'number'
                                    ? `${v}${v <= 24 ? '' : ''}`
                                    : v
                            }
                            label={{
                                value: 'Jam',
                                angle: 90,
                                position: 'insideRight',
                                offset: 20
                            }}
                        />

                        <Tooltip
                            content={<CustomTooltipDaily />}
                            formatter={(val, name) =>formatNumber(Number(val), 'number')
                            }
                            wrapperStyle={{ zIndex: 1000 }}
                        />
                        <Legend 
                            height={30}
                            wrapperStyle={{ bottom: 60 }}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        />

                        {/* Bar untuk Stok */}
                        {/* <Bar yAxisId="left" name="Stok" dataKey="Stok" stackId="a" fill={COLORS.Stok} fillOpacity={stokOpacity} /> */}
                        <Bar yAxisId="left" name="Produksi" dataKey="Produksi" fill={COLORS.Produksi} fillOpacity={produksiOpacity} />
                        <Bar yAxisId="left" name="Pakai" dataKey="Pakai" stackId="a" fill={COLORS.Pakai} fillOpacity={paiOpacity} />
                        <Bar yAxisId="left" name="Kirim" dataKey="Kirim" stackId="a" fill={COLORS.Kirim} fillOpacity={kirimOpacity} />
                        <Scatter
                            yAxisId="right"
                            name="Jam Olah"
                            dataKey="jamOlah"
                            fill={COLORS.Black}
                            shape="cross"
                            fillOpacity={jamOpacity}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default CangkangProduksiHarian;